import { Draft } from 'immer';
import { AppState } from '../interface';
import {
    JarvisAlternativeAvailability,
    JarvisAvcSuccessResponse,
    JarvisProduct,
} from '../../../interface';

/**
 * Sorts tariff by their download speed, deos not modify original array
 *
 * @param tariffs - Tariffs to be sorted
 * @returns A copy of the sorted array
 */
export const sortTariffsByDownloadSpeed = (tariffs: JarvisProduct[]) =>
    ([...tariffs].sort((t1, t2) => t1.download > t2.download ? -1 : 1));

/**
 * Handles the setting of the selected tariff id
 * Sets the `selectedTariffId` if it is included in the `availableTariffs`,
 * sets the fastest from `availabelTariffs` if it is not included
 *
 * @param draft - Draft state to be modified
 * @param selectedTariffId - The selected tariff id from the user
 */
export const handleSetSelectedTariffId = (
    draft: Draft<AppState>,
    selectedTariffId: string,
) => {
    const availableTariffIds = draft.availableTariffs.map(tariff => tariff.id);

    if (availableTariffIds.includes(selectedTariffId)) {
        draft.selectedTariffId = selectedTariffId;
    }
    else if (draft.availableTariffs.length > 0) {
        draft.selectedTariffId = availableTariffIds[0];
    }
};

/**
 * Handles the setting of the available tariffs
 * Sorts the `availableTariffs` by their download speed
 * and recalculates the `selectedTariffId`
 *
 * @param draft - Draft state to be modified
 * @param tariffs - The available tariff
 * @param selectedTariffId - The selected tariff id from the user
 */
export const handleSetAvailableTariffs = (
    draft: Draft<AppState>,
    tariffs: JarvisProduct[],
    selectedTariffId = draft.selectedTariffId,
) => {
    draft.availableTariffs = sortTariffsByDownloadSpeed(tariffs);
    handleSetSelectedTariffId(draft, selectedTariffId);
};

/**
 * Handles the state updates after an availablity check
 * Sets `availableTariffs`, `address`, `customer` and `selectedTVProduct`
 *
 * @param draft - Draft state to be modified
 * @param availabilityResponse - The response of the availability
 */
export const handleSetAvailability = (
    draft: Draft<AppState>,
    availabilityResponse: JarvisAvcSuccessResponse,
    availability: JarvisAlternativeAvailability,
    alternativeTariff?: JarvisProduct,
) => {
    const { products, availableTvConnect, customerInfo, address, marketability, selected } = availabilityResponse;

    handleSetAvailableTariffs(draft, products, selected.id);

    draft.selectedTVProduct = availableTvConnect;
    draft.isTVProductSelected = !!availableTvConnect;
    draft.customer = customerInfo;
    draft.address = address;
    draft.marketability = marketability;
    draft.alternativeTariff = alternativeTariff;
    draft.preSelectedTariff = selected;
    draft.availability = {
        ...availability,
        cable: draft.availableTariffs.length > 0 && !draft.availableTariffs[0].isUnity,
        cableUnity: draft.availableTariffs.length > 0 && draft.availableTariffs[0].isUnity,
    };
};
